import {
  DEEP_DIVE_QUESTION_COUNT,
  type DeepDiveAction,
  type DeepDiveState,
  type MatchPointSession,
} from '../types/deepDive.types';

export const initialDeepDiveState: DeepDiveState = {
  matchPoints: [],
  isComplete: false,
};

export function deepDiveReducer(
  state: DeepDiveState,
  action: DeepDiveAction
): DeepDiveState {
  switch (action.type) {
    case 'COMMIT_MATCH_POINTS': {
      const stages = action.payload;
      if (stages.length === 0) return state;

      const sameAsExisting =
        state.matchPoints.length === stages.length &&
        state.matchPoints.every(
          (session, index) => session.stage === stages[index]
        );
      if (sameAsExisting) return state;

      const matchPoints: MatchPointSession[] = stages.map((stage) => ({
        stage,
        isCompleted: false,
        turns: [{ kind: 'fact-pick', factId: null }],
      }));

      return { matchPoints, isComplete: false };
    }
    case 'PICK_FACT': {
      const { matchPointIndex, factId } = action.payload;
      return updateSession(state, matchPointIndex, (session) => {
        const turns = session.turns.slice();
        const lastIndex = turns.length - 1;
        const lastTurn = turns[lastIndex];
        if (!lastTurn || lastTurn.kind !== 'fact-pick') return session;
        turns[lastIndex] = { ...lastTurn, factId };
        turns.push({ kind: 'question', questionIndex: 0, answer: '' });
        return { ...session, turns };
      });
    }
    case 'ANSWER_QUESTION': {
      const { matchPointIndex, answer } = action.payload;
      return updateSession(state, matchPointIndex, (session) => {
        const turns = session.turns.slice();
        const lastIndex = turns.length - 1;
        const lastTurn = turns[lastIndex];
        if (!lastTurn || lastTurn.kind !== 'question') return session;
        turns[lastIndex] = { ...lastTurn, answer };

        const nextQuestionIndex = lastTurn.questionIndex + 1;
        if (nextQuestionIndex < DEEP_DIVE_QUESTION_COUNT) {
          turns.push({
            kind: 'question',
            questionIndex: nextQuestionIndex,
            answer: '',
          });
        } else {
          turns.push({ kind: 'lesson', lessons: [] });
        }

        return { ...session, turns };
      });
    }
    case 'ADD_LESSON': {
      const { matchPointIndex, lesson } = action.payload;
      const trimmed = lesson.trim();
      if (!trimmed) return state;
      return updateSession(state, matchPointIndex, (session) => {
        const turns = session.turns.slice();
        const lastIndex = turns.length - 1;
        const lastTurn = turns[lastIndex];
        if (!lastTurn || lastTurn.kind !== 'lesson') return session;
        turns[lastIndex] = {
          ...lastTurn,
          lessons: [...lastTurn.lessons, trimmed],
        };
        return { ...session, turns };
      });
    }
    case 'EDIT_LESSON': {
      const { matchPointIndex, lessonIndex, lesson } = action.payload;
      const trimmed = lesson.trim();
      return updateSession(state, matchPointIndex, (session) => {
        const result = getLessonTurn(session, lessonIndex);
        if (!result) return session;
        const { turns, lastIndex, lastTurn } = result;
        const lessons = trimmed
          ? lastTurn.lessons.map((existing, index) =>
              index === lessonIndex ? trimmed : existing
            )
          : lastTurn.lessons.filter((_, index) => index !== lessonIndex);
        turns[lastIndex] = { ...lastTurn, lessons };
        return { ...session, turns };
      });
    }
    case 'DELETE_LESSON': {
      const { matchPointIndex, lessonIndex } = action.payload;
      return updateSession(state, matchPointIndex, (session) => {
        const result = getLessonTurn(session, lessonIndex);
        if (!result) return session;
        const { turns, lastIndex, lastTurn } = result;
        turns[lastIndex] = {
          ...lastTurn,
          lessons: lastTurn.lessons.filter((_, index) => index !== lessonIndex),
        };
        return { ...session, turns };
      });
    }
    case 'COMPLETE_MATCH_POINT': {
      const { matchPointIndex } = action.payload;
      const sessions = state.matchPoints.slice();
      const target = sessions[matchPointIndex];
      if (!target) return state;
      const lastTurn = target.turns[target.turns.length - 1];
      if (
        !lastTurn ||
        lastTurn.kind !== 'lesson' ||
        lastTurn.lessons.length === 0
      ) {
        return state;
      }
      sessions[matchPointIndex] = { ...target, isCompleted: true };
      const isLast = matchPointIndex === sessions.length - 1;
      return {
        matchPoints: sessions,
        isComplete: isLast ? true : state.isComplete,
      };
    }
    case 'RESET':
      return initialDeepDiveState;
    default:
      return state;
  }
}

function updateSession(
  state: DeepDiveState,
  matchPointIndex: number,
  updater: (session: MatchPointSession) => MatchPointSession
): DeepDiveState {
  const sessions = state.matchPoints.slice();
  const target = sessions[matchPointIndex];
  if (!target) return state;
  sessions[matchPointIndex] = updater(target);
  return { ...state, matchPoints: sessions };
}

interface LessonTurnResult {
  turns: MatchPointSession['turns'];
  lastIndex: number;
  lastTurn: Extract<MatchPointSession['turns'][number], { kind: 'lesson' }>;
}

function getLessonTurn(
  session: MatchPointSession,
  lessonIndex: number
): LessonTurnResult | null {
  const turns = session.turns.slice();
  const lastIndex = turns.length - 1;
  const lastTurn = turns[lastIndex];
  if (!lastTurn || lastTurn.kind !== 'lesson') return null;
  if (lessonIndex < 0 || lessonIndex >= lastTurn.lessons.length) return null;
  return { turns, lastIndex, lastTurn };
}
